// billing (ops plan 2026-10-13-billing-system §4.1, §4.8): what Radish bills each location, as staff-edited data. The
// feature catalog and device classes say what can be sold; a plan's versions say, per feature, included, a la carte at
// a price or not offered, and per device class a first-unit and an each-additional price. A published version is never
// edited: saving a draft and publishing it makes the next version, and locations stay on theirs until moved. The rules
// (prices, adjustments, the estimate, entitlements, grace) live in @radish/core/billing; this file is only the wire.
//
// Billing is Stripe, which only the cloud reaches: every billing operation is `availability: 'cloud'`. Slice 1 declared
// the catalog and plan editor operations; slice 4 the billing accounts, location subscriptions and adjustments; slice 5
// the merchant's own view, the card (through Stripe's hosted page) and the invoices.
import { z } from 'zod';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { defineError } from '../../define/errors.ts';
import { secret } from '../../define/secret.ts';
import { ConfigFieldsWritten } from '../config/index.ts';
import {
  AccountId, BillingAccountId, BillingAdjustmentId, BillingInvoiceId, IsoInstant, LocalDate, LocationId, LocationSubscriptionId, Money, PlanId, PlanVersionId, Rate,
} from '../../primitives/codecs.ts';

const BILLING_CLOUD = 'Billing is Stripe, which only the cloud reaches.';

// ---- the catalog -----------------------------------------------------------------------------------------------------

/** A feature's key: lower snake case, stable (entitlement checks and Stripe lookup keys use it). */
export const FeatureKey = z.string().regex(/^[a-z][a-z0-9_]{1,47}$/).meta({ id: 'BillingFeatureKey' });

/** The kinds of unit a location is billed for (core `DEVICE_CLASS_KEYS`). */
export const DeviceClassKey = z
  .enum(['register', 'handheld', 'kds', 'customer_display', 'menu_display', 'kitchen_printer', 'location_node'])
  .meta({ id: 'BillingDeviceClassKey' });

/** Where a device class's units are counted from: Billed devices of a form factor, kitchen printers, or location nodes. */
export const CountedFrom = z
  .discriminatedUnion('source', [
    z.object({ source: z.literal('device'), formFactor: z.enum(['register', 'handheld', 'kds', 'menu_display', 'customer_display']) }),
    z.object({ source: z.literal('kitchen_printer') }),
    z.object({ source: z.literal('location_node') }),
  ])
  .meta({ id: 'BillingCountedFrom' });

const Name = z.string().trim().min(1).max(80);
const Description = z.string().trim().max(500);

export const FeatureView = z
  .object({
    featureKey: FeatureKey,
    name: Name,
    description: Description,
    /** A grouping on the editor ("Staff", "Online", …). */
    category: z.string().trim().min(1).max(40),
    /** Never entitlement-checked and always included: selling, payments, the KDS, printing, reports (core). */
    neverGated: z.boolean(),
    sortOrder: z.int().nonnegative(),
  })
  .meta({ id: 'BillingFeature' });

export const DeviceClassView = z
  .object({ deviceClassKey: DeviceClassKey, name: Name, countedFrom: CountedFrom, sortOrder: z.int().nonnegative() })
  .meta({ id: 'BillingDeviceClass' });

// ---- plans and versions ----------------------------------------------------------------------------------------------

/** A monthly price, or null while staff haven't set it (A56: a blank price blocks publishing). */
const Price = Money.nullable();

export const FeatureTerms = z
  .object({ inclusion: z.enum(['included', 'a_la_carte', 'not_offered']), price: Price })
  .meta({ id: 'BillingFeatureTerms' });

export const DeviceTerms = z.object({ firstUnit: Price, eachAdditional: Price }).meta({ id: 'BillingDeviceTerms' });

/** What one plan version charges (core `PlanTerms`). Monthly only at launch (A57); `year` is modelled for later. */
export const PlanTerms = z
  .object({
    currency: z.enum(['USD', 'CAD']),
    interval: z.enum(['month', 'year']),
    basePrice: Price,
    features: z.record(FeatureKey, FeatureTerms),
    devices: z.partialRecord(DeviceClassKey, DeviceTerms),
  })
  .meta({ id: 'BillingPlanTerms' });

export const PlanVersionView = z
  .object({
    planVersionId: PlanVersionId,
    /** 1, 2, 3, … per plan. A draft has the next number. */
    number: z.int().positive(),
    state: z.enum(['draft', 'published', 'retired']),
    terms: PlanTerms,
    publishedAt: IsoInstant.nullable(),
    retiredAt: IsoInstant.nullable(),
    /** Locations on this version now. */
    locationCount: z.int().nonnegative(),
  })
  .meta({ id: 'BillingPlanVersion' });

export const PlanView = z
  .object({
    planId: PlanId,
    name: Name,
    description: Description,
    /** Public plans are offered to any location; internal ones (Legacy, everything included) only by staff. */
    visibility: z.enum(['public', 'internal']),
    /** The built-in Legacy plan: every feature included, $0, never sent to Stripe, not editable. */
    builtIn: z.boolean(),
    sortOrder: z.int().nonnegative(),
    versions: z.array(PlanVersionView),
  })
  .meta({ id: 'BillingPlan' });

/** One reason a draft can't be published (core `PublishProblem`). */
export const PublishProblem = z
  .object({
    path: z.string(),
    problem: z.enum(['price_blank', 'price_negative', 'currency_mismatch', 'interval_not_sold', 'never_gated_feature_not_included', 'unknown_feature']),
  })
  .meta({ id: 'BillingPublishProblem' });

/** One change publishing makes, for staff to confirm (core `versionChanges`). */
export const VersionChange = z.object({ path: z.string(), from: z.string().nullable(), to: z.string().nullable() }).meta({ id: 'BillingVersionChange' });

// ---- errors ----------------------------------------------------------------------------------------------------------

export const BillingPlanNotFound = defineError({
  code: 'billing.plan_not_found', httpStatus: 404, message: 'errors.billing.plan_not_found', retryable: false,
  details: z.object({ planId: PlanId }),
});
export const BillingVersionNotFound = defineError({
  code: 'billing.version_not_found', httpStatus: 404, message: 'errors.billing.version_not_found', retryable: false,
  details: z.object({ planVersionId: PlanVersionId }),
});
/** The built-in Legacy plan can't be edited or given versions. */
export const BillingPlanBuiltIn = defineError({
  code: 'billing.plan_built_in', httpStatus: 409, message: 'errors.billing.plan_built_in', retryable: false,
  details: z.object({ planId: PlanId }),
});
export const BillingNoDraft = defineError({
  code: 'billing.no_draft', httpStatus: 409, message: 'errors.billing.no_draft', retryable: false,
  details: z.object({ planId: PlanId }),
});
/** The draft has a blank or wrong price, or another problem core's `publishProblems` names. */
export const BillingPublishBlocked = defineError({
  code: 'billing.publish_blocked', httpStatus: 422, message: 'errors.billing.publish_blocked', retryable: false,
  details: z.object({ problems: z.array(PublishProblem) }),
});
/**
 * This server reaches no Stripe (no STRIPE_SECRET_KEY: development, CI, staging until Andrew sets one): publishing would
 * queue prices nobody makes, so it is refused, plainly, instead of looking done.
 */
export const BillingStripeNotConfigured = defineError({
  code: 'billing.stripe_not_configured', httpStatus: 503, message: 'errors.billing.stripe_not_configured', retryable: false,
  details: z.object({}),
});
export const BillingFeatureNeverGated = defineError({
  code: 'billing.feature_never_gated', httpStatus: 409, message: 'errors.billing.feature_never_gated', retryable: false,
  details: z.object({ featureKey: FeatureKey }),
});

// ---- commands --------------------------------------------------------------------------------------------------------

const staffCatalog = {
  permission: 'billing.catalog.manage',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: BILLING_CLOUD,
  emits: [],
  agent: { expose: false },
} as const;

export const SaveFeature = defineCommand({
  ...staffCatalog,
  name: 'billing.saveFeature',
  summary: 'Add a feature to the billing catalog or change its name, description, category or order. A new feature is "Not offered" in every plan until staff choose.',
  input: z.object({ featureKey: FeatureKey, name: Name, description: Description, category: z.string().trim().min(1).max(40), sortOrder: z.int().nonnegative() }),
  output: FeatureView,
  risk: 'low',
  dryRun: false,
  errors: [],
});

export const SaveDeviceClass = defineCommand({
  ...staffCatalog,
  name: 'billing.saveDeviceClass',
  summary: "Rename or reorder a device class. What it counts is fixed by its key.",
  input: z.object({ deviceClassKey: DeviceClassKey, name: Name, sortOrder: z.int().nonnegative() }),
  output: DeviceClassView,
  risk: 'low',
  dryRun: false,
  errors: [],
});

export const SavePlan = defineCommand({
  ...staffCatalog,
  name: 'billing.savePlan',
  summary: "Create a plan, or change a plan's name, description, visibility or order. Its prices live in its versions.",
  input: z.object({
    /** Minted by the caller (UUIDv7) so a retried create replays. */
    planId: PlanId,
    name: Name,
    description: Description,
    visibility: z.enum(['public', 'internal']),
    sortOrder: z.int().nonnegative(),
  }),
  output: PlanView,
  risk: 'low',
  dryRun: false,
  errors: [BillingPlanBuiltIn],
});

export const SaveDraftVersion = defineCommand({
  ...staffCatalog,
  name: 'billing.saveDraftVersion',
  summary: "Save a plan's draft terms (one draft per plan). Blank prices may be saved; they block publishing.",
  input: z.object({ planId: PlanId, terms: PlanTerms }),
  output: PlanView,
  risk: 'low',
  dryRun: false,
  errors: [BillingPlanNotFound, BillingPlanBuiltIn],
});

export const DiscardDraftVersion = defineCommand({
  ...staffCatalog,
  name: 'billing.discardDraftVersion',
  summary: "Throw away a plan's draft.",
  input: z.object({ planId: PlanId }),
  output: PlanView,
  risk: 'low',
  dryRun: false,
  errors: [BillingPlanNotFound, BillingNoDraft],
});

export const PublishVersion = defineCommand({
  ...staffCatalog,
  name: 'billing.publishVersion',
  summary: "Publish a plan's draft as its next version. A dry run returns the changes for staff to confirm. Locations on older versions stay on them until moved.",
  input: z.object({ planId: PlanId }),
  output: z.object({ plan: PlanView, publishedVersionId: PlanVersionId, changes: z.array(VersionChange) }),
  risk: 'medium',
  dryRun: true,
  errors: [BillingPlanNotFound, BillingPlanBuiltIn, BillingNoDraft, BillingPublishBlocked, BillingStripeNotConfigured],
});

export const RetireVersion = defineCommand({
  ...staffCatalog,
  name: 'billing.retireVersion',
  summary: 'Retire a published version: no location can be put on it any more; the locations on it stay until moved.',
  input: z.object({ planVersionId: PlanVersionId }),
  output: PlanView,
  risk: 'medium',
  dryRun: true,
  errors: [BillingVersionNotFound, BillingPlanBuiltIn],
});

// ---- queries ---------------------------------------------------------------------------------------------------------

export const GetBillingCatalog = defineQuery({
  name: 'billing.catalog',
  summary: 'The feature catalog and the device classes, in order.',
  input: z.object({}),
  output: z.object({ features: z.array(FeatureView), deviceClasses: z.array(DeviceClassView) }),
  permission: 'billing.catalog.manage',
  availability: 'cloud',
  cloudReason: BILLING_CLOUD,
  scope: 'platform',
  errors: [],
  agent: { expose: false },
});

export const ListBillingPlans = defineQuery({
  name: 'billing.plans',
  summary: 'Every plan with its versions (draft, published, retired) and how many locations are on each.',
  input: z.object({}),
  output: z.object({ plans: z.array(PlanView) }),
  permission: 'billing.catalog.manage',
  availability: 'cloud',
  cloudReason: BILLING_CLOUD,
  scope: 'platform',
  errors: [],
  agent: { expose: false },
});

// ---- slice 4: billing accounts, location subscriptions and adjustments (§4.1, §4.6) ----------------------------------

/** A Stripe customer id as staff type it in (the customer already billed by hand in the same Stripe account). */
export const StripeCustomerId = z.string().trim().regex(/^cus_[A-Za-z0-9]{6,64}$/).meta({ id: 'StripeCustomerId' });

/** A line of the bill: `base`, `feature:<key>` or `device:<class>` (core `LineKey`). */
export const BillLineKey = z.string().regex(/^(?:base|feature:[a-z][a-z0-9_]{1,47}|device:[a-z_]{2,32})$/).meta({ id: 'BillingLineKey' });

const Reason = z.string().trim().min(1).max(300);

export const BillingAccountView = z
  .object({
    billingAccountId: BillingAccountId,
    /** Who pays, as the invoice names them. */
    name: Name,
    email: z.email().max(254),
    /** The Stripe customer, once made (or the one staff entered). Null until the billing worker makes it. */
    stripeCustomerId: z.string().nullable(),
  })
  .meta({ id: 'BillingAccount' });

/** Who pays: one of the account's billing accounts, or a new one (a new Stripe customer, or the one billed by hand). */
export const BillingAccountChoice = z
  .union([
    z.object({ billingAccountId: BillingAccountId }),
    z.object({
      /** Minted by the caller (UUIDv7) so a retried start names the same billing account. */
      newBillingAccountId: BillingAccountId,
      name: Name,
      email: z.email().max(254),
      /** The customer already billed by hand in the same Stripe account, so its history stays together; null makes a new one. */
      existingStripeCustomerId: StripeCustomerId.nullable(),
    }),
  ])
  .meta({ id: 'BillingAccountChoice' });

export const AdjustmentKind = z.enum(['trial', 'free_period', 'discount']).meta({ id: 'BillingAdjustmentKind' });

export const AdjustmentAmount = z
  .union([z.object({ percent: Rate }), z.object({ fixed: Money })])
  .meta({ id: 'BillingAdjustmentAmount' });

export const AdjustmentView = z
  .object({
    adjustmentId: BillingAdjustmentId,
    locationId: LocationId,
    locationName: z.string(),
    accountName: z.string(),
    adjustmentKind: AdjustmentKind,
    /** One line of the bill, or null for the whole bill (trials and free periods always cover the whole bill). */
    lineKey: BillLineKey.nullable(),
    amount: AdjustmentAmount.nullable(),
    startsOn: LocalDate,
    /** The first date it no longer covers; null for a discount with no end. */
    endsOn: LocalDate.nullable(),
    reason: z.string(),
    grantedByName: z.string(),
    grantedVia: z.enum(['radish_staff', 'reseller']),
    grantedAt: IsoInstant,
    endedAt: IsoInstant.nullable(),
    endedByName: z.string().nullable(),
    endedReason: z.string().nullable(),
  })
  .meta({ id: 'BillingAdjustment' });

export const SubscriptionStatus = z
  .enum(['starting', 'trialing', 'active', 'incomplete', 'past_due', 'canceled', 'reverted'])
  .meta({ id: 'BillingSubscriptionStatus', description: 'starting: saved and waiting for Stripe; reverted: put back on Legacy within its free time.' });

export const LocationSubscriptionView = z
  .object({
    locationSubscriptionId: LocationSubscriptionId,
    status: SubscriptionStatus,
    planId: PlanId,
    planName: Name,
    planVersionId: PlanVersionId,
    versionNumber: z.int().positive(),
    /** The newest published version of the same plan, when it is not this one ("Move to current price"). */
    newerVersionId: PlanVersionId.nullable(),
    newerVersionNumber: z.int().positive().nullable(),
    /** The a la carte features bought. */
    features: z.array(FeatureKey),
    billingAccount: BillingAccountView,
    stripeSubscriptionId: z.string().nullable(),
    /** The first billing date (the end of the free time, or the start). */
    firstBillingOn: LocalDate,
    /** Set to stop at the end of the current period. */
    cancelAtPeriodEnd: z.boolean(),
    startedAt: IsoInstant,
    canceledAt: IsoInstant.nullable(),
    /** When Stripe last matched this subscription; null while a change is still on its way. */
    syncedAt: IsoInstant.nullable(),
    /** The last Stripe failure for this subscription, for staff (a request retries with backoff, then parks). */
    lastError: z.string().nullable(),
  })
  .meta({ id: 'BillingLocationSubscription' });

export const EstimateLineView = z
  .object({ lineKey: BillLineKey, name: z.string(), quantity: z.int().nonnegative(), amount: Money, discount: Money, net: Money })
  .meta({ id: 'BillingEstimateLine' });

/** Core `estimate`: the monthly price, what is charged on starting, and the next invoice, in the location's time zone. */
export const EstimateView = z
  .object({
    monthly: Money,
    dueNow: Money,
    nextBillingOn: LocalDate,
    nextBill: z.object({ subtotal: Money, discountTotal: Money, total: Money, freeBy: BillingAdjustmentId.nullable() }),
    lines: z.array(EstimateLineView),
  })
  .meta({ id: 'BillingEstimate' });

/** A published version a location can be put on (staff see internal plans too; the built-in Legacy plan is never one). */
export const PlanOfferView = z
  .object({
    planId: PlanId, planName: Name, description: Description, visibility: z.enum(['public', 'internal']), planVersionId: PlanVersionId,
    versionNumber: z.int().positive(), terms: PlanTerms,
  })
  .meta({ id: 'BillingPlanOffer' });

/** The day a location's manual billing is paid up to, as staff set it (slice 7, §4.6). */
export const PaidByHandView = z
  .object({ until: LocalDate, note: z.string(), setByName: z.string(), setAt: IsoInstant })
  .meta({ id: 'BillingPaidByHand' });

const locationBillingView = z
  .object({
    location: z.object({
      locationId: LocationId, name: z.string(), timezone: z.string(), currency: z.enum(['USD', 'CAD']), accountName: z.string(),
      /** Today in the location's time zone: free time starts today, and every date here is the location's. */
      today: LocalDate,
    }),
    /** Null: the location is on "Legacy, everything included" (billed by hand, nothing billed by Radish Next). */
    subscription: LocationSubscriptionView.nullable(),
    /** Units counted per device class now (core `billableQuantities`). */
    quantities: z.partialRecord(DeviceClassKey, z.int().nonnegative()),
    offers: z.array(PlanOfferView),
    features: z.array(FeatureView),
    deviceClasses: z.array(DeviceClassView),
    billingAccounts: z.array(BillingAccountView),
    adjustments: z.array(AdjustmentView),
    /** The current subscription's estimate; null on Legacy. */
    estimate: EstimateView.nullable(),
    /** What the caller may grant (A60): staff any length and discounts; a reseller up to 30 days of free time. */
    grantor: z.object({
      via: z.enum(['radish_staff', 'reseller']), maxFreeDays: z.int().positive().nullable(), discounts: z.boolean(),
      /** Whether the caller may record the day the store is paid by hand up to (slice 7; Radish staff only for now). */
      marksPaidByHand: z.boolean().default(false),
    }),
    /**
     * Slice 7 (§4.6): while the location is on Legacy, the day staff say its manual billing is paid up to, or null. It
     * orders Not yet on a plan, and Put on a plan offers it as the free period's end.
     */
    paidByHand: PaidByHandView.nullable().default(null),
  })
  .meta({ id: 'BillingLocation' });
type LocationBillingViewType = typeof locationBillingView;
/**
 * Named, so the catalog's inferred type refers to it instead of spelling it out in every command that answers it (TS7056:
 * seven commands and a query share this output). Its one member restates the base's own, so it is exactly that type.
 */
export interface LocationBillingSchema extends LocationBillingViewType {
  readonly _zod: LocationBillingViewType['_zod'];
}
export const LocationBillingView: LocationBillingSchema = locationBillingView;

export const BillableDeviceView = z
  .object({ deviceId: z.string(), name: z.string() })
  .meta({ id: 'BillingBillableDevice' });

/** A subscription in the Stripe customer that Radish Next did not make: one staff bill by hand and must cancel by hand. */
export const ManualSubscriptionView = z
  .object({
    stripeSubscriptionId: z.string(),
    status: z.string(),
    /** What it bills each interval (the sum of its flat items), or null when Stripe gives no flat amount. */
    amount: Money.nullable(),
    interval: z.string().nullable(),
    description: z.string(),
    createdAt: IsoInstant,
  })
  .meta({ id: 'BillingManualSubscription' });

const FreeTimeInput = z.object({ adjustmentKind: z.enum(['trial', 'free_period']), endsOn: LocalDate, reason: Reason });

// ---- slice 4 errors --------------------------------------------------------------------------------------------------

export const BillingLocationNotFound = defineError({
  code: 'billing.location_not_found', httpStatus: 404, message: 'errors.billing.location_not_found', retryable: false,
  details: z.object({ locationId: LocationId }),
});
/** The location is already on a plan (the database keeps one subscription per location). */
export const BillingAlreadyOnPlan = defineError({
  code: 'billing.already_on_plan', httpStatus: 409, message: 'errors.billing.already_on_plan', retryable: false,
  details: z.object({ locationId: LocationId }),
});
export const BillingNotOnPlan = defineError({
  code: 'billing.not_on_plan', httpStatus: 409, message: 'errors.billing.not_on_plan', retryable: false,
  details: z.object({ locationId: LocationId }),
});
/** The version is a draft, retired, the built-in Legacy plan's, or (for a reseller) an internal plan's. */
export const BillingVersionNotOffered = defineError({
  code: 'billing.version_not_offered', httpStatus: 409, message: 'errors.billing.version_not_offered', retryable: false,
  details: z.object({ planVersionId: PlanVersionId }),
});
export const BillingFeatureNotOffered = defineError({
  code: 'billing.feature_not_offered', httpStatus: 422, message: 'errors.billing.feature_not_offered', retryable: false,
  details: z.object({ featureKey: z.string() }),
});
/**
 * The Stripe customer staff entered has subscriptions Radish Next didn't make (billed by hand). Each must be confirmed
 * as being cancelled by hand before the location is put on a plan, so nobody is billed twice (plan §4.6 step 4).
 */
export const BillingManualSubscriptionsUnconfirmed = defineError({
  code: 'billing.manual_subscriptions_unconfirmed', httpStatus: 409, message: 'errors.billing.manual_subscriptions_unconfirmed', retryable: false,
  details: z.object({ stripeSubscriptionIds: z.array(z.string()) }),
});
export const BillingStripeCustomerNotFound = defineError({
  code: 'billing.stripe_customer_not_found', httpStatus: 404, message: 'errors.billing.stripe_customer_not_found', retryable: false,
  details: z.object({ stripeCustomerId: z.string() }),
});
/** Core `mayGrant` refused (A60), or free time not starting today. */
export const BillingGrantRefused = defineError({
  code: 'billing.grant_refused', httpStatus: 403, message: 'errors.billing.grant_refused', retryable: false,
  details: z.object({
    refusal: z.enum([
      'not_own_account', 'reseller_no_discounts', 'reseller_over_limit', 'needs_end_date', 'ends_before_start', 'needs_amount', 'percent_over_100',
      'percent_too_precise', 'starts_in_past', 'free_time_starts_today', 'line_not_billed',
    ]),
  }),
});
/** Going back to Legacy is only possible while the free time runs; after the first billing date, cancel instead. */
export const BillingFreeTimeOver = defineError({
  code: 'billing.free_time_over', httpStatus: 409, message: 'errors.billing.free_time_over', retryable: false,
  details: z.object({ locationId: LocationId }),
});
export const BillingAdjustmentNotFound = defineError({
  code: 'billing.adjustment_not_found', httpStatus: 404, message: 'errors.billing.adjustment_not_found', retryable: false,
  details: z.object({ adjustmentId: BillingAdjustmentId }),
});
/** A price the location would be charged is blank (core `priceLines`). Published versions never have one. */
export const BillingPriceBlank = defineError({
  code: 'billing.price_blank', httpStatus: 422, message: 'errors.billing.price_blank', retryable: false,
  details: z.object({ path: z.string() }),
});

// ---- slice 4 commands ------------------------------------------------------------------------------------------------

const subscriptionCommand = {
  permission: 'billing.subscription.manage',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: BILLING_CLOUD,
  emits: [],
  agent: { expose: false },
} as const;

export const StartSubscription = defineCommand({
  ...subscriptionCommand,
  name: 'billing.startSubscription',
  summary:
    "Put a location on a published plan version: its add-ons, who pays, and a trial or free period with an end date and a reason. Stripe bills nothing before the free time ends (trial end = first billing date). With an existing Stripe customer, every subscription in it that Radish Next didn't make must be confirmed as being cancelled by hand.",
  input: z.object({
    locationId: LocationId,
    planVersionId: PlanVersionId,
    features: z.array(FeatureKey).max(64),
    billingAccount: BillingAccountChoice,
    freeTime: FreeTimeInput.nullable(),
    /** The manual subscriptions (from `billing.stripeCustomer`) staff confirm they are cancelling by hand. */
    confirmedManualSubscriptionIds: z.array(z.string().max(255)).max(50),
  }),
  output: LocationBillingView,
  risk: 'high',
  dryRun: true,
  errors: [
    BillingLocationNotFound, BillingAlreadyOnPlan, BillingVersionNotOffered, BillingFeatureNotOffered, BillingPriceBlank, BillingGrantRefused,
    BillingManualSubscriptionsUnconfirmed, BillingStripeCustomerNotFound, BillingStripeNotConfigured,
  ],
});

export const RevertToLegacy = defineCommand({
  ...subscriptionCommand,
  name: 'billing.revertToLegacy',
  summary: 'Put a location back on "Legacy, everything included" while its free time still runs: its Stripe subscription is cancelled at once and nothing is charged.',
  input: z.object({ locationId: LocationId, reason: Reason }),
  output: LocationBillingView,
  risk: 'high',
  dryRun: true,
  errors: [BillingLocationNotFound, BillingNotOnPlan, BillingFreeTimeOver, BillingStripeNotConfigured],
});

export const SetPaidByHand = defineCommand({
  ...subscriptionCommand,
  name: 'billing.setPaidByHand',
  summary:
    "Record the day a location still on Legacy has its manual billing paid up to, with a note, or clear it (null). Nothing changes when the day comes: Not yet on a plan turns the row amber, and Put on a plan offers the day as the free period's end.",
  input: z.object({ locationId: LocationId, until: LocalDate.nullable(), note: z.string().trim().max(300) }),
  output: LocationBillingView,
  risk: 'high',
  dryRun: true,
  errors: [BillingLocationNotFound, BillingAlreadyOnPlan],
});

export const ChangeSubscription = defineCommand({
  ...subscriptionCommand,
  name: 'billing.changeSubscription',
  summary:
    "Change a location's plan, move it to its plan's current price version, or change the add-ons it buys. The change prorates onto the next invoice, or is invoiced now when staff choose.",
  input: z.object({
    locationId: LocationId,
    planVersionId: PlanVersionId,
    features: z.array(FeatureKey).max(64),
    proration: z.enum(['next_invoice', 'invoice_now']),
  }),
  output: LocationBillingView,
  risk: 'high',
  dryRun: true,
  errors: [BillingLocationNotFound, BillingNotOnPlan, BillingVersionNotOffered, BillingFeatureNotOffered, BillingPriceBlank, BillingStripeNotConfigured],
});

export const CancelSubscription = defineCommand({
  ...subscriptionCommand,
  name: 'billing.cancelSubscription',
  summary: "Cancel a location's subscription at the end of its current period (it can be resumed until then), or now.",
  input: z.object({ locationId: LocationId, when: z.enum(['period_end', 'now']), reason: Reason }),
  output: LocationBillingView,
  risk: 'high',
  dryRun: true,
  errors: [BillingLocationNotFound, BillingNotOnPlan, BillingStripeNotConfigured],
});

export const ResumeSubscription = defineCommand({
  ...subscriptionCommand,
  name: 'billing.resumeSubscription',
  summary: 'Keep a subscription that was set to cancel at the end of its period.',
  input: z.object({ locationId: LocationId }),
  output: LocationBillingView,
  risk: 'medium',
  dryRun: true,
  errors: [BillingLocationNotFound, BillingNotOnPlan, BillingStripeNotConfigured],
});

const adjustmentCommand = { ...subscriptionCommand, permission: 'billing.adjustment.grant' } as const;

export const GrantAdjustment = defineCommand({
  ...adjustmentCommand,
  name: 'billing.grantAdjustment',
  summary:
    "Grant a location's subscription a trial, a free period or a discount, with an end date and a reason; who granted it is recorded. Staff grant any length and discounts; a reseller up to 30 days of free time on its own accounts, and no discounts (A60). Free time starts today.",
  input: z.object({
    /** Minted by the caller (UUIDv7) so a retried grant names the same adjustment. */
    adjustmentId: BillingAdjustmentId,
    locationId: LocationId,
    adjustmentKind: AdjustmentKind,
    lineKey: BillLineKey.nullable(),
    amount: AdjustmentAmount.nullable(),
    startsOn: LocalDate,
    endsOn: LocalDate.nullable(),
    reason: Reason,
  }),
  output: LocationBillingView,
  risk: 'high',
  dryRun: true,
  errors: [BillingLocationNotFound, BillingNotOnPlan, BillingGrantRefused, BillingStripeNotConfigured],
});

export const EndAdjustment = defineCommand({
  ...adjustmentCommand,
  name: 'billing.endAdjustment',
  summary: 'End a trial, free period or discount today, with a reason. Who ended it is recorded.',
  input: z.object({ adjustmentId: BillingAdjustmentId, reason: Reason }),
  output: LocationBillingView,
  risk: 'high',
  dryRun: true,
  errors: [BillingAdjustmentNotFound, BillingGrantRefused, BillingStripeNotConfigured],
});

// ---- slice 4 queries -------------------------------------------------------------------------------------------------

const subscriptionQuery = {
  permission: 'billing.subscription.manage',
  availability: 'cloud',
  cloudReason: BILLING_CLOUD,
  scope: 'location',
  agent: { expose: false },
} as const;

export const GetLocationBilling = defineQuery({
  ...subscriptionQuery,
  name: 'billing.location',
  summary: "A location's plan, add-ons, device counts, estimate, adjustments and who pays, with the plans it can be put on (the staff Plans & Billing view).",
  input: z.object({ locationId: LocationId }),
  output: LocationBillingView,
  errors: [BillingLocationNotFound],
});

export const EstimateBilling = defineQuery({
  ...subscriptionQuery,
  name: 'billing.estimate',
  summary: "What a location would pay on a plan version with these add-ons and this free time, from today's device counts (core `estimate`).",
  input: z.object({ locationId: LocationId, planVersionId: PlanVersionId, features: z.array(FeatureKey).max(64), freeTimeEndsOn: LocalDate.nullable() }),
  output: EstimateView,
  errors: [BillingLocationNotFound, BillingVersionNotOffered, BillingFeatureNotOffered, BillingPriceBlank],
});

export const ListBillableDevices = defineQuery({
  ...subscriptionQuery,
  name: 'billing.billableDevices',
  summary: 'The units counted for one device class at a location (DLG-081): live devices with Billed on of its form factor, kitchen printers or location nodes.',
  input: z.object({ locationId: LocationId, deviceClassKey: DeviceClassKey }),
  output: z.object({ deviceClassKey: DeviceClassKey, name: Name, devices: z.array(BillableDeviceView) }),
  errors: [BillingLocationNotFound],
});

export const GetStripeCustomer = defineQuery({
  ...subscriptionQuery,
  scope: 'account',
  name: 'billing.stripeCustomer',
  summary:
    "The read-only check at switch-over: one Stripe customer, and its subscriptions still running that Radish Next didn't make (billed by hand), read from Stripe at this moment for this one customer.",
  input: z.object({ stripeCustomerId: StripeCustomerId }),
  output: z.object({ stripeCustomerId: z.string(), name: z.string().nullable(), email: z.string().nullable(), manualSubscriptions: z.array(ManualSubscriptionView) }),
  errors: [BillingStripeCustomerNotFound, BillingStripeNotConfigured],
});

export const ListBillingAdjustments = defineQuery({
  name: 'billing.adjustmentsLog',
  summary: 'Every trial, free period and discount ever granted, newest first: who granted it, why, from and to, and who ended it.',
  input: z.object({}),
  output: z.object({ adjustments: z.array(AdjustmentView) }),
  permission: 'billing.catalog.manage',
  availability: 'cloud',
  cloudReason: BILLING_CLOUD,
  scope: 'platform',
  errors: [],
  agent: { expose: false },
});

// ---- slice 7: Not yet on a plan (§4.6 step 1, IMPROVE L440) ------------------------------------------------------------

export const NotYetOnPlanRowView = z
  .object({
    locationId: LocationId,
    locationName: z.string(),
    accountId: AccountId,
    accountName: z.string(),
    resellerName: z.string(),
    /** Units counted now per device class (core `billableQuantities`), only the classes with any. */
    devices: z.array(z.object({ deviceClassKey: DeviceClassKey, name: z.string(), count: z.int().positive() })),
    /** The location's date of its device switch-over to Radish Next, or null when it never migrated. */
    migratedOn: LocalDate.nullable(),
    daysOnLegacy: z.int().nonnegative().nullable(),
    paidByHand: PaidByHandView.nullable().default(null),
    /** Core `notYetOnPlanStanding` at the location's today: `due` turns the row amber, and nothing else changes. */
    standing: z.enum(['due', 'waiting', 'no_date']),
  })
  .meta({ id: 'BillingNotYetOnPlanRow' });

export const ListNotYetOnPlan = defineQuery({
  name: 'billing.notYetOnPlan',
  summary:
    'Every live location still on "Legacy, everything included" (no subscription of ours), so none is forgotten: account, reseller, devices by class, days since its switch-over and the day its manual billing is paid up to. Radish staff see every one; a reseller only those of its own accounts.',
  input: z.object({}),
  output: z.object({ locations: z.array(NotYetOnPlanRowView) }),
  // Whoever may put a store on a plan reads the list. Who holds this key is decided in one place, the server's staff
  // and reseller policy (server/src/platform/identity/access.ts).
  permission: 'billing.subscription.manage',
  availability: 'cloud',
  cloudReason: BILLING_CLOUD,
  scope: 'platform',
  errors: [],
  agent: { expose: false },
});

// ---- slice 5: the merchant's view, the card and the invoices (§4.5, §4.7) ----------------------------------------------

/** Who reads a location's own billing: anyone who may manage the location's settings (legacy `manage_location_settings`). */
const MERCHANT_VIEW = 'settings.location.manage';

/** The card on file, as Stripe describes it: never the number, only its brand, last four digits and expiry. */
export const CardSummaryView = z
  .object({ brand: z.string().max(40), last4: z.string().regex(/^\d{4}$/), expMonth: z.int().min(1).max(12), expYear: z.int().min(2000).max(2200) })
  .meta({ id: 'BillingCardSummary' });

/**
 * Where the location stands after a failed card (core `dunningState`, A59): `grace` is day n of N (counted from 0, the
 * day it failed) and the add-ons stay on; `grace_ended` pauses the a la carte add-ons bought (`pausedFeatures`), never a
 * core feature.
 */
export const DunningView = z
  .discriminatedUnion('state', [
    z.object({ state: z.literal('ok') }),
    z.object({ state: z.literal('grace'), day: z.int().nonnegative(), of: z.int().positive(), endsOn: LocalDate, failedOn: LocalDate }),
    z.object({ state: z.literal('grace_ended'), endedOn: LocalDate, failedOn: LocalDate, pausedFeatures: z.array(FeatureKey) }),
  ])
  .meta({ id: 'BillingDunning' });

export const MerchantFeatureView = z
  .object({
    featureKey: FeatureKey,
    name: Name,
    /** included in the plan, bought a la carte, sold a la carte but not bought ("Add for $x/mo"), or bought and paused after grace. */
    standing: z.enum(['included', 'bought', 'available', 'paused']),
    /** The monthly price of an a la carte feature; null when included. */
    price: Money.nullable(),
  })
  .meta({ id: 'BillingMerchantFeature' });

/** What a merchant sees of its location's billing (SCR-129 merchant view, G-B2: read-only; slice 5). */
export const MerchantBillingView = z
  .object({
    location: z.object({ locationId: LocationId, name: z.string(), timezone: z.string(), currency: z.enum(['USD', 'CAD']), today: LocalDate }),
    /** Null: on "Legacy, everything included", billed by the account representative (an empty state, never an error: B293). */
    plan: z
      .object({
        planName: Name,
        versionNumber: z.int().positive(),
        status: SubscriptionStatus,
        firstBillingOn: LocalDate,
        cancelAtPeriodEnd: z.boolean(),
        billingName: z.string(),
        billingEmail: z.string(),
      })
      .nullable(),
    features: z.array(MerchantFeatureView),
    /** Units counted per device class and their monthly charge on the plan (core `priceLines`). */
    devices: z.array(z.object({ deviceClassKey: DeviceClassKey, name: Name, quantity: z.int().nonnegative(), amount: Money })),
    /** The trial or free period running today, in plain words on the page. */
    freeTime: z.object({ adjustmentKind: z.enum(['trial', 'free_period']), endsOn: LocalDate }).nullable(),
    discounts: z.array(z.object({ name: z.string(), amount: AdjustmentAmount, endsOn: LocalDate.nullable() })),
    estimate: EstimateView.nullable(),
    card: CardSummaryView.nullable(),
    dunning: DunningView,
    /**
     * Whether a Stripe customer pays for the location, so a card can be put on it. Updating it also needs
     * `billing.payment_method.manage` (the Super Admin's), which the server checks on the command.
     */
    hasCustomer: z.boolean(),
  })
  .meta({ id: 'BillingMerchantView' });

export const InvoiceView = z
  .object({
    invoiceId: BillingInvoiceId,
    /** Stripe's invoice number (`ABCD-0001`); null while a draft. */
    number: z.string().nullable(),
    status: z.enum(['draft', 'open', 'paid', 'uncollectible', 'void']),
    /** The day it was issued, in the location's time zone. */
    issuedOn: LocalDate,
    periodStart: LocalDate.nullable(),
    periodEnd: LocalDate.nullable(),
    total: Money,
    amountDue: Money,
    amountPaid: Money,
    /** Whether a PDF can be downloaded (Stripe makes one once the invoice is finalized). */
    hasPdf: z.boolean(),
  })
  .meta({ id: 'BillingInvoice' });

export const BillingInvoiceNotFound = defineError({
  code: 'billing.invoice_not_found', httpStatus: 404, message: 'errors.billing.invoice_not_found', retryable: false,
  details: z.object({ invoiceId: z.string() }),
});
/** No Stripe customer pays for this location yet (on Legacy, or still being made): there is no card to update (B293). */
export const BillingNoCustomer = defineError({
  code: 'billing.no_customer', httpStatus: 409, message: 'errors.billing.no_customer', retryable: false,
  details: z.object({ locationId: LocationId }),
});
/** The card page Stripe hosted was not finished, was for another customer or location, or saved no card. */
export const BillingCardUpdateIncomplete = defineError({
  code: 'billing.card_update_incomplete', httpStatus: 409, message: 'errors.billing.card_update_incomplete', retryable: false,
  details: z.object({ reason: z.enum(['not_found', 'not_complete', 'not_this_location', 'no_card']) }),
});

/** GATE-08: a merchant reads its location's Plans & Billing only once the location is on a real plan. */
export const BillingMerchantNotOnPlan = defineError({
  code: 'billing.merchant_not_on_plan', httpStatus: 403, message: 'errors.billing.merchant_not_on_plan', retryable: false,
  details: z.object({ locationId: LocationId }),
});

const merchantQuery = {
  permission: MERCHANT_VIEW,
  availability: 'cloud',
  cloudReason: BILLING_CLOUD,
  scope: 'location',
  agent: { expose: false },
} as const;

export const GetMerchantBilling = defineQuery({
  ...merchantQuery,
  name: 'billing.summary',
  summary:
    "The merchant's read-only Plans & Billing (G-B2): the plan, features (included, bought, or offered with a price), devices and their charge, free time and discounts in plain words, the estimate, the card on file and where a failed card stands. It never calls Stripe.",
  input: z.object({ locationId: LocationId }),
  output: MerchantBillingView,
  errors: [BillingLocationNotFound, BillingMerchantNotOnPlan],
});

export const ListInvoices = defineQuery({
  ...merchantQuery,
  name: 'billing.invoices',
  summary: "The location's invoices from Radish, newest first, as Stripe last reported them (mirrored from its events, never computed by us).",
  input: z.object({ locationId: LocationId }),
  output: z.object({ invoices: z.array(InvoiceView) }),
  errors: [BillingLocationNotFound, BillingMerchantNotOnPlan],
});

export const GetInvoicePdf = defineQuery({
  ...merchantQuery,
  name: 'billing.invoicePdf',
  summary: "A short-lived link to one of the location's own invoices as a PDF, read from Stripe when asked.",
  input: z.object({ locationId: LocationId, invoiceId: BillingInvoiceId }),
  output: z.object({ url: z.url({ protocol: /^https$/ }) }),
  errors: [BillingLocationNotFound, BillingInvoiceNotFound, BillingStripeNotConfigured, BillingMerchantNotOnPlan],
});

const cardCommand = {
  permission: 'billing.payment_method.manage',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: BILLING_CLOUD,
  emits: [],
  agent: { expose: false },
} as const;

export const StartCardUpdate = defineCommand({
  ...cardCommand,
  name: 'billing.startCardUpdate',
  summary:
    "Open Stripe's own card page for the account's Super Admin: Stripe takes the card (Radish never sees its number) and sends the person back to Plans & Billing. The link is handed over once.",
  input: z.object({ locationId: LocationId }),
  output: z.object({
    /** Stripe's hosted page for this person, valid about a day; a replay of the command leaves it out. */
    url: secret(z.url({ protocol: /^https$/ })).optional(),
    checkoutSessionId: z.string(),
  }),
  risk: 'medium',
  dryRun: true,
  errors: [BillingLocationNotFound, BillingNoCustomer, BillingStripeNotConfigured],
});

export const FinishCardUpdate = defineCommand({
  ...cardCommand,
  name: 'billing.finishCardUpdate',
  summary:
    "Back from Stripe's card page: the card it saved becomes the one Radish bills, and any invoice still open is tried on it. Stripe's own event does the same if the person never comes back.",
  input: z.object({ locationId: LocationId, checkoutSessionId: z.string().regex(/^cs_[A-Za-z0-9_]{6,250}$/) }),
  output: z.object({ card: CardSummaryView }),
  risk: 'medium',
  dryRun: true,
  errors: [BillingLocationNotFound, BillingNoCustomer, BillingCardUpdateIncomplete, BillingStripeNotConfigured],
});

// ---- slice 6: entitlements (§4.5) ---------------------------------------------------------------------------------------

/** An a la carte feature the location could add, at its plan's monthly price (core `EntitlementOffer`). */
export const EntitlementOfferView = z.object({ featureKey: FeatureKey, name: Name, price: Money }).meta({ id: 'BillingEntitlementOffer' });

/**
 * The fields of the config entity `billing.entitlements` (id = the location id; contexts/config `BILLING_FIELDS`): what
 * the node and every register hold of the location's entitlements, offline too (core `EntitlementFacts`).
 */
export const EntitlementsFields = z
  .object({
    locationId: LocationId,
    planName: Name,
    /** Off on "Legacy, everything included" (every migrated location until staff put it on a real plan): everything on. */
    enforcement: z.enum(['off', 'on']),
    /** The features on when enforced: never-gated, included, and bought (not paused). */
    features: z.array(FeatureKey).max(128),
    paused: z.array(FeatureKey).max(128),
    offers: z.array(EntitlementOfferView).max(128),
  })
  .meta({ id: 'BillingEntitlementsFields' });

export const EntitlementsView = EntitlementsFields.extend({
  /** Whether the location is on a real plan (not Legacy): GATE-08 shows a merchant Plans & Billing only then. */
  onPlan: z.boolean(),
}).meta({ id: 'BillingEntitlements' });

/**
 * The upsell for a feature a location is not entitled to (§4.5), from legacy's welcome pages (which no link reached):
 * the headline and the call to action, priced from the location's plan. Features legacy had no page for use `generic`.
 * Display copy both clients share; whether to show it is core's `featureStanding`.
 */
export const FEATURE_UPSELL: Readonly<Record<string, { readonly title: string; readonly headline: string; readonly cta: string }>> = {
  gift_cards: { title: 'Gift Cards', headline: 'Boost sales with custom gift cards.', cta: 'Add Gift Cards' },
  online_ordering: { title: 'Commission-free online food ordering', headline: 'Launch a native online ordering site integrated with your POS.', cta: 'Add Online Ordering' },
  timecards_payroll: { title: 'Timecard Tracking', headline: 'Enable seamless clocking at the register.', cta: 'Add Timecard Tracking' },
};

export const GetEntitlements = defineQuery({
  name: 'billing.entitlements',
  summary:
    "What the location may use, as its node and registers hold it (the last entitlements synced from the cloud): the plan's name, whether enforcement is on, the features on, the add-ons paused and the upsell offers. Nothing synced reads as Legacy, everything on.",
  input: z.object({ locationId: LocationId }),
  output: EntitlementsView,
  permission: { session: 'user' },
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: false },
});

export const RefreshEntitlements = defineCommand({
  name: 'billing.refreshEntitlements',
  summary:
    "Work out a location's entitlements from its billing (Legacy with nothing on a plan; else core's entitlements on its plan version, add-ons and grace) and write them for its node and registers when they changed. The import runs it for every migrated location, which puts it on Legacy.",
  input: z.object({ locationId: LocationId }),
  output: EntitlementsView,
  permission: 'billing.subscription.manage',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: BILLING_CLOUD,
  emits: [ConfigFieldsWritten],
  errors: [BillingLocationNotFound],
  risk: 'low',
  dryRun: true,
  agent: { expose: false },
});

export const billingCommands = [
  SaveFeature, SaveDeviceClass, SavePlan, SaveDraftVersion, DiscardDraftVersion, PublishVersion, RetireVersion,
  StartSubscription, RevertToLegacy, ChangeSubscription, CancelSubscription, ResumeSubscription, GrantAdjustment, EndAdjustment,
  StartCardUpdate, FinishCardUpdate, RefreshEntitlements, SetPaidByHand,
] as const;
export const billingQueries = [
  GetBillingCatalog, ListBillingPlans, GetLocationBilling, EstimateBilling, ListBillableDevices, GetStripeCustomer, ListBillingAdjustments,
  GetMerchantBilling, ListInvoices, GetInvoicePdf, GetEntitlements, ListNotYetOnPlan,
] as const;
